/**
 * ApplyMappingSetPanel — "Apply to imported items" on a Field Mappings set card.
 *
 * First says what the set would do to the items already imported from the
 * connector (a dry run), then applies it on request and says what it did
 * (lib/fieldMappingApplyLater). Items imported before their source values were
 * saved can be matched by reading a Miro board again.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout, Field, Select } from '@/components/ds';
import { btnPrimary, btnSecondary } from '@/components/mono/primitives';
import type { FieldMappingSet } from '../../../lib/fieldMappingRegistry';
import { plural } from '../../../lib/fieldMappingApply';
import {
  applyMappingSetToImported,
  planMappingSetForWorkspace,
  readMiroBoardAgain,
  type ApplyLaterResult,
  type BoardReadResult,
  type ImportedForMapping,
  type MappingSetPlan,
} from '../../../lib/fieldMappingApplyLater';
import { formatIntegrationsNetworkError, getIntegrationsApi, type MiroBoard } from '../../../lib/integrationsApi';

const CONNECTORS_PATH = '/prioritization/settings/connectors';

export interface ApplyMappingSetPanelProps {
  set: FieldMappingSet;
  /** Only 'miro' records what each source record said today. */
  connectorType: string;
  connectorLabel: string;
  isConnected: boolean;
  workspaceId: string;
}

export function ApplyMappingSetPanel({ set, connectorType, connectorLabel, isConnected, workspaceId }: ApplyMappingSetPanelProps) {
  const [plan, setPlan] = useState<MappingSetPlan | null>(null);
  const [unrecorded, setUnrecorded] = useState<ImportedForMapping['unrecorded']>([]);
  const [preview, setPreview] = useState<ApplyLaterResult | null>(null);
  const [result, setResult] = useState<ApplyLaterResult | null>(null);
  const [busy, setBusy] = useState<'planning' | 'applying' | 'reading' | null>('planning');
  const [error, setError] = useState<string | null>(null);

  const [boards, setBoards] = useState<MiroBoard[] | null>(null);
  const [boardId, setBoardId] = useState('');
  const [boardsError, setBoardsError] = useState<string | null>(null);
  const [readResult, setReadResult] = useState<(BoardReadResult & { boardName: string }) | null>(null);

  const replan = useCallback(async () => {
    setBusy('planning');
    setError(null);
    try {
      const planned = await planMappingSetForWorkspace(workspaceId, connectorType, set);
      setPlan(planned.plan);
      setUnrecorded(planned.unrecorded);
      setPreview(await applyMappingSetToImported(planned.plan, { dryRun: true, sourceLabel: connectorLabel }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the imported items.');
    } finally {
      setBusy(null);
    }
  }, [workspaceId, connectorType, set, connectorLabel]);

  useEffect(() => {
    void replan();
  }, [replan]);

  const apply = async () => {
    if (!plan) return;
    setBusy('applying');
    setError(null);
    try {
      setResult(await applyMappingSetToImported(plan, { dryRun: false, sourceLabel: connectorLabel }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The set could not be applied.');
    } finally {
      setBusy(null);
    }
  };

  const loadBoards = async () => {
    setBoardsError(null);
    try {
      const list = await getIntegrationsApi(workspaceId).listMiroBoards();
      setBoards(list);
      setBoardId(list[0]?.id ?? '');
    } catch (err) {
      setBoardsError(formatIntegrationsNetworkError(err));
    }
  };

  const readBoard = async () => {
    const board = boards?.find((b) => b.id === boardId);
    if (!board) return;
    setBusy('reading');
    setBoardsError(null);
    try {
      const read = await readMiroBoardAgain(workspaceId, board, unrecorded);
      setReadResult({ ...read, boardName: board.name });
      setResult(null);
      await replan();
    } catch (err) {
      setBoardsError(formatIntegrationsNetworkError(err));
    } finally {
      setBusy(null);
    }
  };

  const willWrite = !!preview && preview.counts.given + preview.counts.changed > 0;
  const shown = result ?? preview;

  return (
    <div className="border-t border-[var(--rule)] bg-[var(--surface)] px-4 py-4 space-y-3 text-sm text-[var(--text-sec)]">
      <p className="text-xs text-[var(--text-ter)]">
        Applies “{set.name}” to items already imported from {connectorLabel}. Values a person set are left alone;
        titles and descriptions are never changed.
      </p>

      {busy === 'planning' && <p className="text-[var(--text-ter)]">Reading the imported items…</p>}
      {error && <Callout tone="coral">{error}</Callout>}

      {busy !== 'planning' && plan && plan.items.length === 0 && !result && (
        <p>No items from {connectorLabel} have their sticky values saved yet.</p>
      )}
      {busy !== 'planning' && shown && plan && plan.items.length > 0 && (
        <Callout tone={shown.hasProblems ? 'mustard' : 'neutral'} label={result ? 'Applied' : 'Before you apply'}>
          {shown.message}
        </Callout>
      )}

      {plan && plan.items.length > 0 && !result && (
        <button type="button" {...btnPrimary()} onClick={apply} disabled={!willWrite || busy !== null}>
          {busy === 'applying' ? 'Applying…' : 'Apply'}
        </button>
      )}

      {busy !== 'planning' && unrecorded.length > 0 && (
        <div className="space-y-2 pt-1">
          <p>
            {plural(unrecorded.length, 'item')} came from {connectorLabel} before their sticky values were saved, so
            this set can’t reach {unrecorded.length === 1 ? 'it' : 'them'} yet. Reading the board again matches each
            sticky to its item by title.
          </p>
          {!isConnected ? (
            <p className="text-[var(--text-ter)]">
              {connectorLabel} is not connected.{' '}
              <Link to={CONNECTORS_PATH} className="font-medium underline hover:no-underline">
                Connect it
              </Link>{' '}
              to read a board again.
            </p>
          ) : boards === null ? (
            <button type="button" {...btnSecondary()} onClick={loadBoards} disabled={busy !== null}>
              Read a board again
            </button>
          ) : boards.length === 0 ? (
            <p className="text-[var(--text-ter)]">No boards found in {connectorLabel}.</p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Board" className="min-w-[16rem]">
                <Select
                  size="sm"
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  options={boards.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
              <button type="button" {...btnSecondary()} onClick={readBoard} disabled={!boardId || busy !== null}>
                {busy === 'reading' ? 'Reading…' : 'Read board'}
              </button>
            </div>
          )}
          {boardsError && <p className="text-[var(--coral-text)]">{boardsError}</p>}
        </div>
      )}

      {readResult && (
        <p>
          Read “{readResult.boardName}”: {plural(readResult.matched, 'sticky', 'stickies')} matched an item
          {readResult.unmatched > 0 ? `, ${readResult.unmatched} matched no item` : ''}
          {readResult.ambiguous > 0 ? `, ${readResult.ambiguous} matched more than one and were left out` : ''}.
          {readResult.recordError && (
            <span className="text-[var(--coral-text)]"> Their values could not be saved: {readResult.recordError}</span>
          )}
        </p>
      )}
    </div>
  );
}
